import React, { useEffect, useRef, useState, useMemo } from "react";

/**
 * LipSyncMorphing — canvas-rendered mouth/eye animation synced to an
 * HTMLAudioElement via audioRef.  Timeline uses {time, duration, viseme}.
 *
 * Props
 * ─────
 *  timeline   – array of {time:number, duration:number, viseme:string}
 *  audioRef   – React ref whose .current is the <audio> element (single source of truth)
 *  size       – base canvas size (max 900)
 *  showDebug  – overlay timing info
 */
export default function LipSyncMorphing({
  timeline = [],
  audioRef = null,
  size = 900,
  showDebug = false,
}) {
  const canvasRef = useRef(null);
  const [images, setImages] = useState({});
  const [loaded, setLoaded] = useState(false);
  const blinkRef = useRef({ next: 0, on: false, start: 0, dur: 0.10 });

  /* ---- mouth / eye sprite filenames ---- */
  const files = {
    aa:"aa.png", ae:"ae.png", aw:"aw.png", ay:"ay.png",
    ee:"ee.png", er:"er.png", ey:"ey.png", fv:"fv.png",
    h:"h.png", k:"k.png", l:"l.png", m:"m.png", o:"o.png",
    ow:"ow.png", oy:"oy.png", p:"p.png", r:"r.png", s:"s.png",
    sh:"sh.png", sil:"sil.png", th:"th.png", w:"w.png",
    eyes_open:"eyes_open.png", eyes_closed:"eyes_closed.png",
    smile_E:"smile_E.png",
  };

  /* ---- preload sprites ---- */
  useEffect(() => {
    let cancelled = false;
    const keys = Object.keys(files);
    Promise.all(keys.map(k => new Promise(res => {
      const img = new Image();
      img.onload = () => res([k, img]);
      img.onerror = () => res([k, null]);
      img.src = `/assets/mouths/${files[k]}`;
    }))).then(entries => {
      if (cancelled) return;
      const map = {}; entries.forEach(([k, img]) => map[k] = img);
      setImages(map); setLoaded(true);
    });
    return () => { cancelled = true; };
  }, []);

  /* ---- initialise blink timer ---- */
  useEffect(() => {
    const now = performance.now() / 1000;
    blinkRef.current.next = now + 3 + Math.random() * 3;
  }, []);

  /* ---- normalise + sort timeline ---- */
  /* Accepts both {time, duration} (completion cache) and {start, end} (interruption cache) */
  const sortedTimeline = useMemo(() => {
    if (!Array.isArray(timeline)) return [];
    return timeline
      .filter(s => {
        const hasTime = Number.isFinite(Number(s?.time)) || Number.isFinite(Number(s?.start));
        const hasDur  = Number.isFinite(Number(s?.duration)) || Number.isFinite(Number(s?.end));
        return hasTime && hasDur;
      })
      .map(s => {
        const t   = Number(s.time ?? s.start ?? 0);
        const dur = Number(s.duration ?? (s.end != null ? Number(s.end) - Number(s.start ?? 0) : 0));
        return {
          time:     Math.max(0, +t),
          duration: Math.max(0, +dur),
          viseme:   String(s.viseme || "sil").toLowerCase(),
        };
      })
      .sort((a, b) => a.time - b.time);
  }, [timeline]);

  /* ---- binary-search: find viseme at time t ---- */
  function visemeAt(t) {
    const arr = sortedTimeline;
    if (!arr.length) return "sil";

    let lo = 0, hi = arr.length - 1;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      const seg = arr[mid];
      if (t < seg.time) {
        hi = mid - 1;
      } else if (t >= seg.time + seg.duration) {
        lo = mid + 1;
      } else {
        return seg.viseme;          // inside this segment
      }
    }
    return "sil";                   // gap between segments or past end
  }

  /* ---- 50 ms lookahead: return next 2-3 upcoming visemes ---- */
  function lookahead(t, count = 3) {
    const AHEAD = 0.05; // 50 ms
    const arr = sortedTimeline;
    const upcoming = [];
    for (let i = 0; i < arr.length && upcoming.length < count; i++) {
      const seg = arr[i];
      if (seg.time >= t && seg.time <= t + AHEAD + seg.duration) {
        upcoming.push(seg);
      }
    }
    return upcoming;
  }

  /* ──────────────── main render loop ──────────────── */
  useEffect(() => {
    if (!loaded) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const dpr = window.devicePixelRatio || 1;
    const W = Math.min(size, 900), H = Math.min(size * 0.75, 700);
    canvas.width = W * dpr; canvas.height = H * dpr;
    canvas.style.width = "100%"; canvas.style.height = "auto";
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    // Draw eyes_open immediately — eliminates the one-frame black gap before RAF starts
    ctx.fillStyle = "#000";
    ctx.fillRect(0, 0, W, H);
    if (images["eyes_open"]) ctx.drawImage(images["eyes_open"], 0, 0, W, H);

    let raf, lastVis = "sil", crossfade = 1.0;
    let fromImg = null, toImg = images["sil"];
    let lastVisChangeWall = 0;            // wall-clock of last viseme switch (seconds)
    const MIN_HOLD = 0.04;               // 40 ms minimum hold per viseme
    const isEyeViseme = (v) => v === "eyes_open" || v === "eyes_closed" || v === "smile_E";

    const draw = () => {
      /* ---- get audio time (single source of truth) ---- */
      const audio = audioRef?.current;
      const isPlaying = audio ? (!audio.paused && !audio.ended && audio.readyState >= 3) : false;
      const t = audio ? audio.currentTime : 0;

      /* ---- if audio is buffering, hold current frame ---- */
      if (audio && !audio.paused && !audio.ended && audio.readyState < 3) {
        raf = requestAnimationFrame(draw);
        return;
      }

      const now = performance.now() / 1000;

      /* ---- natural blinking (3-6 s interval) ---- */
      if (!blinkRef.current.on && now >= blinkRef.current.next) {
        blinkRef.current.on = true;
        blinkRef.current.start = now;
        blinkRef.current.dur = 0.12 + Math.random() * 0.08;
      }
      if (blinkRef.current.on && now > blinkRef.current.start + blinkRef.current.dur) {
        blinkRef.current.on = false;
        blinkRef.current.next = now + 3 + Math.random() * 3;
      }

      /* ---- determine target viseme ---- */
      const vis = visemeAt(t);

      // Kick off lookahead pre-calc (warms image decode cache)
      if (isPlaying) lookahead(t);

      const hasActiveSpeech = isPlaying && sortedTimeline.length > 0;
      const isBlinking = blinkRef.current.on;

      let targetVis;
      if (isBlinking) {
        targetVis = "eyes_closed";
      } else if (hasActiveSpeech && vis !== "sil") {
        targetVis = vis;
      } else if (isPlaying && !sortedTimeline.length) {
        // If the backend returns playable audio without phoneme timing, keep
        // the avatar alive instead of freezing on eyes_open.
        const fallback = ["aa", "ee", "o", "m", "sil"];
        targetVis = fallback[Math.floor(t * 10) % fallback.length];
      } else {
        targetVis = "eyes_open";
      }

      /* ---- transition gating ---- */
      const isBlink = (targetVis === "eyes_closed" || lastVis === "eyes_closed");
      const canTransition = isBlink || ((now - lastVisChangeWall) >= MIN_HOLD);

      if (targetVis !== lastVis && canTransition) {
        fromImg = toImg;
        toImg = images[targetVis] || images["eyes_open"];
        // Smooth crossfade only for eye transitions (blinks are slow enough to blend).
        // Mouth-to-mouth transitions are instant to keep lip sync frame-accurate.
        const smooth = isEyeViseme(targetVis) || isEyeViseme(lastVis);
        crossfade = smooth ? 0.0 : 1.0;
        lastVis = targetVis;
        lastVisChangeWall = now;
      }

      /* ---- render ---- */
      ctx.fillStyle = "#000";
      ctx.fillRect(0, 0, W, H);

      const speed = hasActiveSpeech ? 0.7 : 0.55;
      crossfade = Math.min(1.0, crossfade + speed);
      const eased = crossfade * crossfade * (3.0 - 2.0 * crossfade);

      ctx.globalAlpha = 1.0;
      if (toImg) ctx.drawImage(toImg, 0, 0, W, H);

      if (crossfade < 1.0 && fromImg && toImg && fromImg !== toImg) {
        ctx.globalAlpha = Math.max(0.0, 1.0 - eased);
        ctx.drawImage(fromImg, 0, 0, W, H);
      }
      ctx.globalAlpha = 1.0;

      if (showDebug) {
        ctx.fillStyle = "#0f0"; ctx.font = "0.75rem monospace";
        ctx.fillText(
          `t=${t.toFixed(2)} vis=${vis} target=${targetVis} cf=${crossfade.toFixed(2)} blink=${isBlinking} rdy=${audio?.readyState ?? '-'}`,
          10, 18
        );
      }

      raf = requestAnimationFrame(draw);
    };

    raf = requestAnimationFrame(draw);
    return () => { if (raf) cancelAnimationFrame(raf); };
  }, [loaded, images, size, audioRef, showDebug, sortedTimeline]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        width: "100%", height: "auto",
        borderRadius: "0.75rem", boxShadow: "0 0.5rem 1.25rem rgba(0,0,0,.25)",
        background: "#000",
        visibility: loaded ? "visible" : "hidden",
      }}
    />
  );
}
